"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type VariantProps } from "class-variance-authority";
import { CheckIcon, CopyIcon } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

/**
 * `CopyToClipboardButton`コンポーネントのプロパティの型定義。
 * @interface CopyToClipboardButtonProps
 * @extends Omit<React.ComponentProps<"button"> & VariantProps<typeof buttonVariants>, "onClick"> - 標準のbuttonプロパティと`buttonVariants`の型を継承し、`onClick`プロパティを除外します。
 * @property {string} textToCopy - クリップボードにコピーするテキスト。
 * @property {string} [tooltipText] - ボタンにマウスオーバーした際に表示されるツールチップのテキスト。指定されない場合はデフォルトのテキストが使用されます。
 * @property {string} [successToastText] - テキストのコピー成功時に表示されるトースト通知のテキスト。指定されない場合はデフォルトのテキストが使用されます。
 * @property {React.ReactNode} [children] - ボタンの内部コンテンツをカスタマイズするための子要素。指定されない場合はコピーアイコンまたはチェックアイコンが表示されます。
 */
interface CopyToClipboardButtonProps
  extends Omit<
    React.ComponentProps<"button"> & VariantProps<typeof buttonVariants>,
    "onClick"
  > {
  textToCopy: string;
  tooltipText?: string;
  successToastText?: string;
  children?: React.ReactNode;
  tooltipSide?: "top" | "right" | "bottom" | "left";
  showTooltip?: boolean; // ツールチップ表示制御
  disableHoverableContent?: boolean; // ツールチップのホバー動作制御
  showToast?: boolean; // トースト表示制御
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onCopied?: () => void;
}

/**
 * 指定されたテキストをクリップボードにコピーする機能を持つボタンコンポーネント。
 * コピー操作の成功・失敗に応じてトースト通知を表示し、ボタンのアイコンを一時的に変更します。
 *
 * @param {CopyToClipboardButtonProps} props - コンポーネントのプロパティ。
 * @param {string} props.textToCopy - クリップボードにコピーするテキスト。
 * @param {string} [props.tooltipText] - ボタンのツールチップに表示するテキスト。
 * @param {string} [props.successToastText] - コピー成功時に表示するトーストメッセージ。
 * @param {VariantProps<typeof buttonVariants>["variant"]} [props.variant="ghost"] - ボタンの表示バリアント。
 * @param {VariantProps<typeof buttonVariants>["size"]} [props.size="icon-sm"] - ボタンのサイズ。
 * @param {string} [props.className] - ボタンに適用する追加のCSSクラス。
 * @param {React.ReactNode} [props.children] - ボタンのアイコンの代わりに表示するカスタムコンテンツ。
 * @returns {JSX.Element} `CopyToClipboardButton`コンポーネント。
 */
export function CopyToClipboardButton({
  textToCopy,
  tooltipText,
  successToastText,
  variant = "ghost",
  size = "icon-sm",
  className,
  children,
  tooltipSide = "top",
  showTooltip = true, // デフォルトでツールチップを表示
  disableHoverableContent = false, // デフォルトではホバー可能
  showToast = true, // デフォルトでトーストを表示
  onClick,
  onCopied,
  ...props
}: CopyToClipboardButtonProps) {
  const { t } = useLingui();
  const [hasCopied, setHasCopied] = React.useState(false);

  const defaultTooltipText = t(msg`Copy to clipboard`);
  const finalTooltipText = tooltipText || defaultTooltipText;

  const defaultSuccessToastText = t(msg`Copied to clipboard!`);
  const finalSuccessToastText = successToastText || defaultSuccessToastText;

  const handleCopy = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setHasCopied(true);
        if (showToast) {
          toast.success(finalSuccessToastText);
        }
        onCopied?.();

        // ボタンのフォーカスを外す
        e.currentTarget?.blur();

        // 2秒後にアイコンを元に戻す
        setTimeout(() => {
          setHasCopied(false);
        }, 2000);
      } catch (err) {
        logger.error("Failed to copy text: ", err);
        if (showToast) {
          toast.error(t(msg`Failed to copy`), {
            description: t(msg`Could not copy text to clipboard.`),
          });
        }
      }
    },
    [textToCopy, finalSuccessToastText, onCopied, showToast, t],
  );

  const handleClick = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      await handleCopy(e);
    },
    [handleCopy, onClick],
  );

  // showTooltipがfalseの場合はTooltipなしで直接Buttonを返す
  if (!showTooltip) {
    return (
      <Button
        variant={variant}
        size={size}
        className={cn(className)}
        onClick={handleClick}
        aria-label={finalTooltipText}
        {...props}
      >
        {children ? (
          children
        ) : hasCopied ? (
          <CheckIcon className="size-3.5" />
        ) : (
          <CopyIcon className="size-3.5" />
        )}
      </Button>
    );
  }

  return (
    <Tooltip disableHoverableContent={disableHoverableContent}>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn(className)}
          onClick={handleClick}
          aria-label={finalTooltipText}
          {...props}
        >
          {children ? (
            children
          ) : hasCopied ? (
            <CheckIcon className="size-3.5 text-green-600 dark:text-green-400" />
          ) : (
            <CopyIcon className="size-3.5" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{finalTooltipText}</TooltipContent>
    </Tooltip>
  );
}
